/**
 * Espejo frontend del helper canónico de "puntas" financieras.
 *
 * El backend tiene `backend/src/utils/financialFlow.js` con la lógica
 * idéntica. Mantener AMBOS en sync — si cambia la regla, cambiar los dos.
 *
 * Round 2026-05-06 (Fase 2 del refactor "intermediario afecta puntas").
 *
 * Convención de tipos (heredada de `pagado_por`/`pagado_a` JSONB):
 *   tipo=0 → Aloja Fácil
 *   tipo=1 → Cliente (el cliente-gestor de la propiedad)
 *   tipo=2 → Arrendatario / huésped
 *   tipo=3 → Aseador
 *   tipo=4 → Trabajador (empleado sin login: mantención, supervisor, contador)
 *   tipo=5 → SOBRECARGADO: el significado lo decide la TABLA, no el número.
 *
 * ⚠️ Desambiguación de `tipo=5` (round 2026-07-26). Son DOS cosas distintas y
 * NO tienen relación entre sí:
 *
 *   • En `gastos` (Expense) → **RECEPTOR EXTERNO**: cualquier OTRA EMPRESA que
 *     recibe plata de AF (internet, agua, impuestos, un proveedor, o el seed
 *     "Otro"). Trae `recipient_id` → `expense_recipients.id`, y el modelo lo
 *     exige obligatorio, así que en `gastos` un tipo=5 NUNCA es una lavandería.
 *   • En `lavados` (Lavado) → **LAVANDERÍA del catálogo** (AF interna o Genérica
 *     Externa). Trae `usuario_id` → `lavanderias.id` y nunca `recipient_id`.
 *
 * "Receptor externo" NO es "lavandería externa": los pagos a una lavandería se
 * registran en /lavanderia, jamás como receptor externo. Ver
 * `assertExpenseNoApuntaALavanderia` en backend/src/utils/lavadoValidations.js.
 *
 * Ver `docs/flujo-financiero.md §10.7` y §10.2.
 */

export interface PagoTipo {
  tipo: number;
  usuario_id?: number;
  /** Solo en `gastos` con tipo=5: FK a `expense_recipients.id`. */
  recipient_id?: number | null;
}

export type RolAF = "paga" | "recibe" | "intermediario" | null | undefined;

export interface MovimientoInput {
  monto: number;
  rolAF?: RolAF;
  pagadoPor?: PagoTipo | null;
  pagadoA?: PagoTipo | null;
}

const AF_TIPO = 0;

function esAF(entidad: PagoTipo | null | undefined): boolean {
  return entidad?.tipo === AF_TIPO;
}

function mismaEntidad(
  a: PagoTipo | null | undefined,
  b: PagoTipo | null | undefined,
): boolean {
  if (!a || !b) return false;
  if (a.tipo !== b.tipo) return false;
  if ([0, 1, 2].includes(a.tipo)) return true;
  // tipo=5 se identifica por `recipient_id` cuando la fila viene de `gastos`
  // (receptor externo) y por `usuario_id` cuando viene de `lavados`
  // (lavandería). Comparar solo `usuario_id` hacía indistinguibles a dos
  // receptores externos distintos, porque el suyo es null.
  if (a.tipo === 5 && (a.recipient_id != null || b.recipient_id != null)) {
    return a.recipient_id != null && a.recipient_id === b.recipient_id;
  }
  return a.usuario_id != null && a.usuario_id === b.usuario_id;
}

/**
 * Devuelve `{ ingreso, egreso, neto }` desde la perspectiva de Aloja
 * Fácil empresa para un movimiento. Reglas:
 *   - rolAF=null → AF no participa, todo 0.
 *   - rolAF=paga → solo egreso.
 *   - rolAF=recibe → solo ingreso.
 *   - rolAF=intermediario → ingreso == egreso (neto 0). Las dos puntas
 *     se contabilizan para reportes de volumen / conciliación.
 */
export function flujoParaAF(mov: MovimientoInput | null | undefined): {
  ingreso: number;
  egreso: number;
  neto: number;
} {
  const monto = Math.abs(Number(mov?.monto || 0));
  if (monto === 0) return { ingreso: 0, egreso: 0, neto: 0 };

  const rolAF = mov?.rolAF ?? null;
  const pagadoPor = mov?.pagadoPor ?? null;
  const pagadoA = mov?.pagadoA ?? null;

  if (rolAF === "intermediario") {
    let ingreso = 0;
    let egreso = 0;
    if (pagadoPor && !esAF(pagadoPor)) ingreso += monto;
    if (pagadoA && !esAF(pagadoA)) egreso += monto;
    return { ingreso, egreso, neto: ingreso - egreso };
  }
  if (rolAF === "paga") {
    if (pagadoA && !esAF(pagadoA)) {
      return { ingreso: 0, egreso: monto, neto: -monto };
    }
    return { ingreso: 0, egreso: 0, neto: 0 };
  }
  if (rolAF === "recibe") {
    if (pagadoPor && !esAF(pagadoPor)) {
      return { ingreso: monto, egreso: 0, neto: monto };
    }
    return { ingreso: 0, egreso: 0, neto: 0 };
  }
  // rolAF null/undefined → AF no participa.
  return { ingreso: 0, egreso: 0, neto: 0 };
}

/**
 * Desde la perspectiva de una entidad específica (cliente, aseador,
 * lavandería). Útil para `/finanzas-cliente`, `/finanzas-aseador`.
 */
export function flujoParaEntidad(
  mov: MovimientoInput | null | undefined,
  entidad: PagoTipo,
): { ingreso: number; egreso: number; neto: number } {
  const monto = Math.abs(Number(mov?.monto || 0));
  if (monto === 0) return { ingreso: 0, egreso: 0, neto: 0 };

  const rolAF = mov?.rolAF ?? null;
  const pagadoPor = mov?.pagadoPor ?? null;
  const pagadoA = mov?.pagadoA ?? null;

  // Para una entidad no-AF, lo que ve depende de si está en pagadoPor o
  // pagadoA y de si hay AF en el medio (intermediario, paga, recibe).
  if (rolAF === "intermediario") {
    // Tramo 1 (pagadoPor → AF): si la entidad es pagadoPor, ve egreso a AF.
    // Tramo 2 (AF → pagadoA): si la entidad es pagadoA, ve ingreso de AF.
    if (mismaEntidad(pagadoPor, entidad)) {
      return { ingreso: 0, egreso: monto, neto: -monto };
    }
    if (mismaEntidad(pagadoA, entidad)) {
      return { ingreso: monto, egreso: 0, neto: monto };
    }
    return { ingreso: 0, egreso: 0, neto: 0 };
  }
  if (rolAF === "paga") {
    // AF → pagadoA. Solo pagadoA ve ingreso (de AF).
    if (mismaEntidad(pagadoA, entidad)) {
      return { ingreso: monto, egreso: 0, neto: monto };
    }
    return { ingreso: 0, egreso: 0, neto: 0 };
  }
  if (rolAF === "recibe") {
    // pagadoPor → AF. Solo pagadoPor ve egreso (a AF).
    if (mismaEntidad(pagadoPor, entidad)) {
      return { ingreso: 0, egreso: monto, neto: -monto };
    }
    return { ingreso: 0, egreso: 0, neto: 0 };
  }
  // rolAF=null → flujo directo entre las partes.
  if (mismaEntidad(pagadoPor, entidad)) {
    return { ingreso: 0, egreso: monto, neto: -monto };
  }
  if (mismaEntidad(pagadoA, entidad)) {
    return { ingreso: monto, egreso: 0, neto: monto };
  }
  return { ingreso: 0, egreso: 0, neto: 0 };
}

/**
 * Suma de N movimientos para una perspectiva. AF o entidad específica.
 */
export function agregarFlujos(
  movimientos: Array<MovimientoInput> | null | undefined,
  perspectiva: "AF" | PagoTipo,
): { ingreso: number; egreso: number; neto: number } {
  let ingreso = 0;
  let egreso = 0;
  for (const mov of movimientos || []) {
    const r =
      perspectiva === "AF"
        ? flujoParaAF(mov)
        : flujoParaEntidad(mov, perspectiva);
    ingreso += r.ingreso;
    egreso += r.egreso;
  }
  return { ingreso, egreso, neto: ingreso - egreso };
}
